import React from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const PurchaseReceipt = ({ request, onClose }) => {
  const formatCurrency = (amount) => {
    return '₦' + new Intl.NumberFormat('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return '';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDepartment = (department) => {
    if (!department) return '';
    return department.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatStatus = (status) => {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatStage = (stage) => {
    const stages = {
      'request_creation': 'Request Creation',
      'approval_stage': 'Department Approval',
      'processing_stage': 'Processing',
      'procurement_stage': 'Procurement Review',
      'finance_stage': 'Finance Approval',
      'pay_vendor_stage': 'Payment Processing',
      'delivery_stage': 'Delivery Confirmation',
      'completed': 'Completed'
    };
    return stages[stage] || stage;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_approval': { letter: 'P', color: 'bg-yellow-500', label: 'Pending Approval' },
      'pending_manager_approval': { letter: 'M', color: 'bg-orange-500', label: 'Manager Approval' },
      'pending_procurement_review': { letter: 'R', color: 'bg-purple-500', label: 'Procurement Review' },
      'pending_finance_approval': { letter: 'F', color: 'bg-indigo-500', label: 'Finance Approval' },
      'payment_in_progress': { letter: '₦', color: 'bg-blue-500', label: 'Payment Processing' },
      'awaiting_delivery': { letter: 'D', color: 'bg-teal-500', label: 'Awaiting Delivery' },
      'completed': { letter: '✓', color: 'bg-green-500', label: 'Completed' },
      'rejected': { letter: 'X', color: 'bg-red-500', label: 'Rejected' },
      'cancelled': { letter: 'C', color: 'bg-gray-500', label: 'Cancelled' }
    };
    
    return statusConfig[status] || { letter: 'N', color: 'bg-gray-400', label: formatStatus(status) };
  };

  const handleDownload = async () => {
    const element = document.getElementById('receipt-content');

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: document.documentElement.offsetWidth
      });

      const imgData = canvas.toDataURL('image/png');

      // PDF setup
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Calculate image dimensions for the PDF width
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // If content height is less than one page, simple add
      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        // Add image in slices across multiple pages
        let position = 0;
        let remainingHeight = imgHeight;
        let pageIndex = 0;

        while (remainingHeight > 0) {
          const sourceY = (position / imgHeight) * canvas.height;
          const sliceHeightPx = Math.min((pageHeight / imgHeight) * canvas.height, canvas.height - sourceY);

          // Create a temporary canvas for each slice
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceHeightPx;
          const ctx = sliceCanvas.getContext('2d');

          ctx.drawImage(
            canvas,
            0,
            sourceY,
            canvas.width,
            sliceHeightPx,
            0,
            0,
            sliceCanvas.width,
            sliceCanvas.height
          );

          const sliceData = sliceCanvas.toDataURL('image/png');

          if (pageIndex > 0) pdf.addPage();
          pdf.addImage(
            sliceData,
            'PNG',
            0,
            0,
            imgWidth,
            (sliceHeightPx / canvas.width) * imgWidth
          );

          position += pageHeight;
          remainingHeight -= pageHeight;
          pageIndex += 1;
        }
      }

      pdf.save(`Purchase_Request_${request.request_id}_Receipt.pdf`);
      if (onClose) onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  if (!request) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-[60] flex items-start justify-center py-8">
      <div className="w-[210mm] mx-auto">
        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mb-4">
          <button
            onClick={handleDownload}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
          >
            Download PDF
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 font-medium"
          >
            Close
          </button>
        </div>

        {/* Receipt Content - A4 Size (210mm x 297mm) */}
        <div id="receipt-content" className="bg-white shadow-2xl min-h-[297mm]">
          {/* Professional Header with Company Branding */}
          <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white p-10">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-6">
                <img 
                  src="/logo.png" 
                  alt="Puffin Group Logo" 
                  className="h-24 w-auto bg-white p-2 rounded-lg shadow-lg"
                  crossOrigin="anonymous"
                />
                <div>
                  <h1 className="text-4xl font-bold tracking-tight mb-2">PUFFIN GROUP LTD</h1>
                  <p className="text-blue-200 text-sm">Enterprise Resource Planning System</p>
                  <p className="text-blue-300 text-xs mt-1">Kano, Nigeria</p>
                </div>
              </div>
              <div className="text-right">
                <div className="bg-white p-2 rounded-lg shadow-lg mb-3">
                  <img 
                    src="/qrcode.png" 
                    alt="QR Code" 
                    className="w-24 h-24 object-contain"
                    crossOrigin="anonymous"
                  />
                </div>
                <p className="text-xs text-blue-200">Scan for verification</p>
              </div>
            </div>
          </div>

          {/* Document Title */}
          <div className="bg-gray-50 border-b-4 border-blue-600 px-10 py-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-1">PURCHASE REQUEST</h2>
                <p className="text-gray-600">Official Transaction Document</p>
              </div>
              <div className={`${getStatusBadge(request.status).color} text-white px-6 py-3 rounded-lg shadow-md`}>
                <p className="text-lg font-bold uppercase tracking-wide">
                  {getStatusBadge(request.status).label}
                </p>
              </div>
            </div>
          </div>

          {/* Request ID and Date Bar */}
          <div className="bg-blue-50 border-b border-blue-200 px-10 py-4">
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Request ID</p>
                <p className="text-xl font-bold text-blue-900">{request.request_id}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Date Created</p>
                <p className="text-lg font-semibold text-gray-900">{formatDate(request.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Current Stage</p>
                <p className="text-lg font-semibold text-gray-900">{formatStage(request.current_stage)}</p>
              </div>
            </div>
          </div>

          {/* Requester Information */}
          <div className="px-10 py-8 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-600">REQUESTER INFORMATION</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Full Name</p>
                <p className="text-base font-semibold text-gray-900">{request.requester_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Email Address</p>
                <p className="text-base text-gray-900">{request.requester_email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Department</p>
                <p className="text-base font-semibold text-gray-900">{formatDepartment(request.department)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Priority Level</p>
                <p className="text-base font-semibold text-gray-900">{request.priority?.toUpperCase() || 'MEDIUM'}</p>
              </div>
            </div>
          </div>

          {/* Request Details */}
          <div className="px-10 py-8 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-600">REQUEST DETAILS</h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-2">Item/Service Description</p>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-base text-gray-900 whitespace-pre-wrap leading-relaxed">{request.item_service_requested}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-6 mt-6">
                <div>
                  <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Quantity</p>
                  <p className="text-lg font-bold text-gray-900">{request.quantity || 1}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Unit Price</p>
                  <p className="text-lg font-bold text-gray-900">{formatCurrency(request.unit_price || request.amount)}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-2 border-green-600">
                  <p className="text-xs text-green-800 font-semibold uppercase mb-1">Total Amount</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(request.amount)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Vendor Information */}
          <div className="px-10 py-8 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-600">VENDOR INFORMATION</h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Vendor Name</p>
                <p className="text-base font-semibold text-gray-900">{request.vendor_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 font-semibold uppercase mb-2">Bank Account Details</p>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-base text-gray-900 whitespace-pre-wrap font-mono">{request.vendor_bank_details}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Approval History */}
          {((request.WorkflowApprovals && request.WorkflowApprovals.length > 0) || (request.workflow_history && request.workflow_history.length > 0) || request.notes?.includes('MD Rejection:') || request.notes?.includes('MD Approval:')) && (
            <div className="px-10 py-8 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-600">APPROVAL WORKFLOW</h3>
              <div className="space-y-3">
                {/* Show workflow history (audit log) if available */}
                {request.workflow_history && request.workflow_history.length > 0 ? (
                  request.workflow_history
                    .filter(entry => {
                      // Only show entries for THIS request
                      if (entry.request_id && entry.request_id !== request.request_id) {
                        return false;
                      }
                      // Only show important workflow actions
                      const importantActions = [
                        'approval_requested',
                        'approved',
                        'rejected',
                        'procurement_review_requested',
                        'procurement_approved',
                        'finance_review_requested',
                        'finance_approved',
                        'routed_to_md_approval',
                        'md_approved',
                        'md_rejected',
                        'payment_authorized',
                        'payment_completed',
                        'delivery_confirmed',
                        'workflow_completed'
                      ];
                      return importantActions.includes(entry.action);
                    })
                    .map((entry, index) => {
                      const displayDate = formatDate(entry.timestamp || entry.created_at);
                      
                      // Format stage name for GMD actions
                      let stageName = entry.stage;
                      let actorName = entry.actor || 'System';
                      
                      if (entry.action === 'md_approved' || entry.action === 'md_rejected') {
                        stageName = 'GMD Approval';
                        actorName = 'GMD';
                      } else if (entry.action === 'approved' && entry.stage === 'approval_stage') {
                        stageName = 'Department Approval';
                      } else if (entry.action === 'procurement_approved' || entry.stage === 'procurement_stage') {
                        stageName = 'Procurement Review';
                      } else if (entry.action === 'finance_approved' || entry.stage === 'finance_stage') {
                        stageName = 'Finance Approval';
                      }
                      
                      return (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-bold text-gray-900">{stageName}</p>
                              <p className="text-sm text-gray-600">{actorName}</p>
                              <p className="text-xs text-gray-500 mt-1">{entry.details || entry.action?.replace(/_/g, ' ')}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              entry.action?.includes('approved') || entry.action === 'md_approved' ? 'bg-green-100 text-green-800' :
                              entry.action?.includes('rejected') || entry.action === 'md_rejected' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {entry.action === 'md_approved' ? 'APPROVE' :
                               entry.action === 'md_rejected' ? 'REJECT' :
                               entry.action?.includes('approved') ? 'APPROVE' :
                               entry.action?.includes('rejected') ? 'REJECT' :
                               'IN PROGRESS'}
                            </span>
                          </div>
                          {displayDate && (
                            <p className="text-xs text-gray-500 mt-2">{displayDate}</p>
                          )}
                        </div>
                      );
                    })
                ) : (
                  /* Fallback to WorkflowApprovals - filter by request_id string */
                  request.WorkflowApprovals
                    .filter(approval => {
                      // Match by request_id string (e.g., "PR-2026-02-0020")
                      return approval.request_id === request.request_id;
                    })
                    .map((approval, index) => {
                      const approverName = approval.approver_name || approval.approver?.first_name || 'System';
                      const displayDate = formatDate(approval.created_at || approval.approved_at);
                      return (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-bold text-gray-900">{formatStage(approval.stage)}</p>
                              <p className="text-sm text-gray-600">{approverName}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              approval.action === 'approve' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {approval.action?.toUpperCase()}
                            </span>
                          </div>
                          {approval.comments && (
                            <p className="text-sm text-gray-700 mt-2 italic">"{approval.comments}"</p>
                          )}
                          {displayDate && (
                            <p className="text-xs text-gray-500 mt-2">{displayDate}</p>
                          )}
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-100 px-10 py-8 text-center border-t-4 border-blue-600">
            <div className="mb-4">
              <p className="text-sm font-bold text-gray-900 mb-2">PUFFIN GROUP LIMITED</p>
              <p className="text-xs text-gray-600">Enterprise Resource Planning System</p>
              <p className="text-xs text-gray-600">Purchase Request Management Module</p>
            </div>
            <div className="border-t border-gray-300 pt-4 mt-4">
              <p className="text-xs text-gray-500">
                This is an official computer-generated document. No signature is required.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Document ID: {request.request_id} | Generated: {formatDate(new Date())}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseReceipt;
